import React from "react";
import type { LargeImageBlock } from "../../../types/content";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./LargeImage.module.css";

/**
 * The widest the image is drawn in each width mode. Contained and fit-to-grid
 * fill the block grid cell (at most the 1168px grid) and do not know its
 * span, so they lead with `auto`: a lazy image then gets its laid-out width
 * from the browser, and one without `sizes=auto` gets the widest case.
 */
function largeImageSizes(
  width: LargeImageBlock["width"],
  fitToGrid: boolean
): string {
  if (fitToGrid) return "auto, (max-width: 1200px) 100vw, 1168px";
  if (width === "narrow") return "auto, (max-width: 900px) 100vw, 900px";
  if (width === "contained") return "auto, (max-width: 1200px) 100vw, 1168px";
  return "100vw";
}

export interface LargeImageProps {
  /**
   * The large image block data from Umbraco
   */
  block: LargeImageBlock;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Large Image component
 * Full-width image section for displaying standalone hero/lifestyle imagery.
 * Use this for visual storytelling, content breaks, and atmosphere.
 * For images with text overlays, use the Hero component instead.
 * Supports border radius for bento grid layouts.
 */
export const LargeImage: React.FC<LargeImageProps> = ({ block, className }) => {
  const {
    image,
    imageAlt,
    aspectRatio,
    width,
    maxHeight,
    caption,
    borderRadius = "none",
    fitToGrid = false,
    mediaType = "image",
    video,
  } = block;

  const isVideo = mediaType === "video" && !!video;

  if (!image && !isVideo) {
    return null;
  }

  // Determine border radius class
  const radiusClass =
    borderRadius && borderRadius !== "none"
      ? styles[`radius-${borderRadius}`]
      : null;

  // When fitToGrid is enabled, the image fills 100% of its parent grid cell
  // and we skip width/aspect/maxHeight since the grid cell controls sizing.
  let containerClasses: string;
  let imageStyle: React.CSSProperties;

  if (fitToGrid) {
    containerClasses = [
      styles.container,
      styles["fit-to-grid"],
      radiusClass,
      className,
    ]
      .filter(Boolean)
      .join(" ");
    imageStyle = {};
  } else {
    // Determine width class (default: full-bleed)
    const widthClass = width ? styles[width] : styles["full-bleed"];

    // Determine aspect ratio class (default: auto)
    const aspectClass = aspectRatio
      ? styles[`aspect-${aspectRatio.replace(":", "-")}`]
      : styles["aspect-auto"];

    containerClasses = [
      styles.container,
      widthClass,
      aspectClass,
      radiusClass,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Custom style for max height
    imageStyle = maxHeight ? { maxHeight: `${maxHeight}px` } : {};
  }

  // Use figcaption if caption is provided
  const hasCaption = caption && caption.trim().length > 0;
  const isDecorative = !imageAlt || imageAlt.trim().length === 0;

  let mediaElement: React.ReactNode;

  if (isVideo) {
    mediaElement = (
      <video
        ref={(el) => {
          if (el) {
            el.muted = true;
            el.defaultMuted = true;
            el.play().catch(() => {});
          }
        }}
        autoPlay
        loop
        muted
        playsInline
        poster={image?.url}
        className={styles.video}
        style={imageStyle}
        src={video.url}
      >
        <track kind="captions" />
      </video>
    );
  } else if (image) {
    // Only include width/height attributes if they have valid values (not 0)
    const imageDimensions: { width?: number; height?: number } = {};
    if (image.width && image.width > 0) {
      imageDimensions.width = image.width;
    }
    if (image.height && image.height > 0) {
      imageDimensions.height = image.height;
    }

    mediaElement = (
      <img
        {...responsiveCmsImage(
          image.url,
          largeImageSizes(width, fitToGrid),
          image.width
        )}
        alt={isDecorative ? "" : imageAlt}
        className={styles.image}
        style={imageStyle}
        loading="lazy"
        {...imageDimensions}
      />
    );
  }

  // Wrap in figure/figcaption if caption exists
  if (hasCaption) {
    return (
      <figure
        className={containerClasses}
        data-component="large-image"
        data-radius={borderRadius}
      >
        {mediaElement}
        <figcaption className={styles.caption}>{caption}</figcaption>
      </figure>
    );
  }

  return (
    <div
      className={containerClasses}
      data-component="large-image"
      data-radius={borderRadius}
    >
      {mediaElement}
    </div>
  );
};

export default LargeImage;
